import type { DeltaLabel, PLSummary } from '@eriko/shared-types';
import { formatCurrency } from '@eriko/shared-types';
import { cn } from '../lib/utils';

// Period-scoped, factual captions — accurate whether the org is brand-new or
// simply had no activity in the selected period (e.g. an empty current week).
// Onboarding guidance lives on the Documents page, not buried in a stat card.
const EMPTY_CAPTION: Record<'revenue' | 'expenses' | 'netProfit', string> = {
  revenue: 'No income this period',
  expenses: 'No expenses this period',
  netProfit: 'No activity this period',
};

interface ReportsStatTrioProps {
  summary: PLSummary;
  loading: boolean;
}

export function ReportsStatTrio({ summary, loading }: ReportsStatTrioProps) {
  return (
    <section
      className={cn(
        'grid grid-cols-1 md:grid-cols-3 mb-12',
        'border border-surface-300/70 rounded-[6px] bg-surface-0/50',
        'divide-y md:divide-y-0 md:divide-x divide-surface-300/70',
      )}
    >
      {loading ? (
        <>
          <StatSkeleton sectionMark="§I" />
          <StatSkeleton sectionMark="§II" />
          <StatSkeleton sectionMark="§III" />
        </>
      ) : (
        <>
          <StatCard
            eyebrow="Revenue"
            eyebrowAccent
            sectionMark="§I"
            value={summary.revenue}
            delta={summary.revenueDelta}
            emptyCaption={EMPTY_CAPTION.revenue}
          />
          <StatCard
            eyebrow="Expenses"
            sectionMark="§II"
            value={summary.expenses}
            delta={summary.expensesDelta}
            emptyCaption={EMPTY_CAPTION.expenses}
          />
          <StatCard
            eyebrow="Net Profit"
            sectionMark="§III"
            value={summary.netProfit}
            delta={summary.netProfitDelta}
            emptyCaption={EMPTY_CAPTION.netProfit}
          />
        </>
      )}
    </section>
  );
}

function StatCard({
  eyebrow,
  eyebrowAccent,
  sectionMark,
  value,
  delta,
  emptyCaption,
}: {
  eyebrow: string;
  eyebrowAccent?: boolean;
  sectionMark: string;
  value: number;
  delta: DeltaLabel;
  emptyCaption: string;
}) {
  const formatted = formatCurrency(value, 2);
  const match = formatted.match(/^(-?\$?)([\d,]+)(\.\d+)?$/);
  const prefix = match?.[1] ?? '$';
  const whole = match?.[2] ?? '0';
  const decimal = match?.[3] ?? '.00';

  const showEmpty = value === 0;
  const deltaClass =
    delta.improved === undefined
      ? 'text-surface-600'
      : delta.improved
        ? 'text-pos'
        : 'text-neg';

  return (
    <div className="relative px-8 py-7">
      <span
        aria-hidden
        className="absolute top-6 right-6 font-serif italic text-[13px] text-surface-500"
      >
        {sectionMark}
      </span>
      <div
        className={cn(
          'font-mono text-[10px] uppercase tracking-[2px] mb-5',
          eyebrowAccent ? 'text-neg' : 'text-surface-600',
        )}
      >
        {eyebrow}
      </div>
      <div className="flex items-baseline text-surface-900">
        <span className="font-serif text-[22px] text-surface-600 mr-1">{prefix}</span>
        <span className="font-serif text-[64px] leading-none tracking-tight">{whole}</span>
        <span className="font-serif text-[22px] text-surface-500">{decimal}</span>
      </div>
      <div className="mt-4 pl-5 relative before:content-[''] before:absolute before:left-0 before:top-[0.7em] before:w-3 before:h-px before:bg-surface-500">
        {showEmpty ? (
          <p className="font-serif italic text-[13px] text-surface-700">{emptyCaption}</p>
        ) : (
          <p className={cn('font-mono text-[12px]', deltaClass)}>{delta.label}</p>
        )}
      </div>
    </div>
  );
}

function StatSkeleton({ sectionMark }: { sectionMark: string }) {
  return (
    <div className="relative px-8 py-7 animate-pulse" aria-hidden>
      <span className="absolute top-6 right-6 font-serif italic text-[13px] text-surface-400">
        {sectionMark}
      </span>
      <div className="h-2.5 w-16 bg-surface-200/80 rounded-sm mb-5" />
      <div className="flex items-baseline gap-1">
        <div className="h-6 w-3 bg-surface-200/80 rounded-sm" />
        <div className="h-14 w-44 bg-surface-200/80 rounded-sm" />
        <div className="h-5 w-10 bg-surface-200/60 rounded-sm" />
      </div>
      <div className="mt-4 pl-5 relative before:content-[''] before:absolute before:left-0 before:top-[0.7em] before:w-3 before:h-px before:bg-surface-400">
        <div className="h-3 w-32 bg-surface-200/80 rounded-sm" />
      </div>
    </div>
  );
}
